import type { Command, InsertEntryKey } from "./interpreter";
import { lastLineIndex, lineLength } from "./model/buffer";
import { clampCursor } from "./model/cursor";
import type { EditorState } from "./model/editor-state";
import { popRedo, popUndo, pushUndoSnapshot } from "./model/history";
import { getMark, withMark } from "./model/marks";
import type { Range } from "./model/range";
import { getRegister, UNNAMED_REGISTER, withRegister } from "./model/registers";
import { MOTIONS } from "./motions";
import { classAt } from "./motions/word-class";
import { applyChange } from "./operators/change";
import { applyDelete } from "./operators/delete";
import { applyPut } from "./operators/put";
import { applyYank } from "./operators/yank";
import { findNextMatch, wordUnderCursor } from "./search";
import { TEXT_OBJECTS } from "./textobjects";

/** 挿入モードへ入る際のカーソル位置と、行追加を伴う場合のバッファを決定する。 */
function enterInsert(state: EditorState, entry: InsertEntryKey): EditorState {
  const { buffer, cursor } = state;
  const line = cursor.line;

  switch (entry) {
    case "i":
      return { ...state, mode: "insert", cursor };
    case "a": {
      const col = Math.min(cursor.col + 1, lineLength(buffer, line));
      return { ...state, mode: "insert", cursor: { line, col } };
    }
    case "I": {
      const text = buffer.lines[line] ?? "";
      const firstNonBlank = text.search(/\S/);
      const col = firstNonBlank === -1 ? 0 : firstNonBlank;
      return { ...state, mode: "insert", cursor: { line, col } };
    }
    case "A": {
      const col = lineLength(buffer, line);
      return { ...state, mode: "insert", cursor: { line, col } };
    }
    case "o": {
      const newLines = [...buffer.lines.slice(0, line + 1), "", ...buffer.lines.slice(line + 1)];
      return {
        ...state,
        buffer: { lines: newLines },
        mode: "insert",
        cursor: { line: line + 1, col: 0 },
      };
    }
    case "O": {
      const newLines = [...buffer.lines.slice(0, line), "", ...buffer.lines.slice(line)];
      return { ...state, buffer: { lines: newLines }, mode: "insert", cursor: { line, col: 0 } };
    }
    default:
      return state;
  }
}

function resolveRange(state: EditorState, command: Extract<Command, { kind: "operator" }>): Range {
  const { target } = command;
  if (target.kind === "self-lines") {
    const times = Math.max(target.count ?? 1, 1);
    const endLine = Math.min(state.cursor.line + times - 1, lastLineIndex(state.buffer));
    return { start: state.cursor, end: { line: endLine, col: 0 }, kind: "linewise" };
  }

  if (target.kind === "text-object") {
    const fn = TEXT_OBJECTS.get(target.name);
    const range = fn ? fn(state.buffer, state.cursor) : null;
    // 対象が見つからない場合（例: カーソルが括弧の外）は何もしない（範囲ゼロ）。
    return range ?? { start: state.cursor, end: state.cursor, kind: "exclusive" };
  }

  // `cw`特殊ケース: カーソルが単語の途中にあるとき、`cw`は`dw`+挿入ではなく`ce`と同じ
  // 挙動になる（末尾の空白を含めない）。本家vimの既知の仕様。
  if (
    command.operator === "c" &&
    target.motion === "w" &&
    classAt(state.buffer, state.cursor) !== "blank"
  ) {
    const endMotion = MOTIONS.get("e");
    if (endMotion) {
      return endMotion(state.buffer, state.cursor, target.count);
    }
  }

  const motion = MOTIONS.get(target.motion);
  if (!motion) {
    return { start: state.cursor, end: state.cursor, kind: "exclusive" };
  }
  return motion(state.buffer, state.cursor, target.count, target.argument);
}

const OPERATOR_APPLY = {
  d: applyDelete,
  c: applyChange,
  y: applyYank,
} as const;

/** 確定した Command を EditorState へ適用する（純関数）。 */
export function execute(state: EditorState, command: Command): EditorState {
  switch (command.kind) {
    case "motion": {
      const motion = MOTIONS.get(command.motion);
      if (!motion) {
        return state;
      }
      const range = motion(state.buffer, state.cursor, command.count, command.argument);
      const cursor = clampCursor(state.buffer, range.end);
      const lastFind =
        command.motion === "f" || command.motion === "t"
          ? command.argument
            ? { variant: command.motion, char: command.argument, direction: "forward" as const }
            : state.lastFind
          : state.lastFind;
      return { ...state, cursor, lastFind };
    }

    case "operator": {
      const range = resolveRange(state, command);
      const isMutating = command.operator !== "y";
      const history = isMutating
        ? pushUndoSnapshot(state.history, { buffer: state.buffer, cursor: state.cursor })
        : state.history;
      const apply = OPERATOR_APPLY[command.operator];
      const result = apply(state.buffer, range);
      const registerName = command.register ?? UNNAMED_REGISTER;
      let registers = withRegister(state.registers, registerName, result.content);
      if (registerName !== UNNAMED_REGISTER) {
        // 本家vimと同様、名前付きレジスタへ書き込んだ内容は無名レジスタにも反映する。
        registers = withRegister(registers, UNNAMED_REGISTER, result.content);
      }
      const cursor = clampCursor(result.buffer, result.cursor);
      const base = { ...state, buffer: result.buffer, cursor, registers, history };
      return command.operator === "c" ? { ...base, mode: "insert" } : base;
    }

    case "simple-edit": {
      const count = Math.max(command.count ?? 1, 1);
      const line = state.buffer.lines[state.cursor.line] ?? "";
      const endCol = Math.min(state.cursor.col + count, line.length);
      if (endCol <= state.cursor.col) {
        return state;
      }
      const history = pushUndoSnapshot(state.history, {
        buffer: state.buffer,
        cursor: state.cursor,
      });
      const range: Range = {
        start: state.cursor,
        end: { line: state.cursor.line, col: endCol },
        kind: "exclusive",
      };
      const result = applyDelete(state.buffer, range);
      const registers = withRegister(state.registers, UNNAMED_REGISTER, result.content);
      const cursor = clampCursor(result.buffer, result.cursor);
      return { ...state, buffer: result.buffer, cursor, registers, history };
    }

    case "enter-insert": {
      const history = pushUndoSnapshot(state.history, {
        buffer: state.buffer,
        cursor: state.cursor,
      });
      return enterInsert({ ...state, history }, command.entry);
    }

    case "history": {
      if (command.op === "undo") {
        const popped = popUndo(state.history);
        if (!popped) {
          return { ...state, statusMessage: "これ以上元に戻せません" };
        }
        const redoHistory = {
          ...popped.history,
          redoStack: [...popped.history.redoStack, { buffer: state.buffer, cursor: state.cursor }],
        };
        return {
          ...state,
          buffer: popped.snapshot.buffer,
          cursor: popped.snapshot.cursor,
          history: redoHistory,
        };
      }
      const popped = popRedo(state.history);
      if (!popped) {
        return { ...state, statusMessage: "これ以上やり直せません" };
      }
      const undoHistory = {
        ...popped.history,
        undoStack: [...popped.history.undoStack, { buffer: state.buffer, cursor: state.cursor }],
      };
      return {
        ...state,
        buffer: popped.snapshot.buffer,
        cursor: popped.snapshot.cursor,
        history: undoHistory,
      };
    }

    case "enter-command-line":
      return { ...state, mode: "command-line", commandLine: command.prefix };

    case "search-repeat": {
      if (!state.lastSearch) {
        return { ...state, statusMessage: "直前の検索がありません" };
      }
      const direction =
        (command.reversed
          ? state.lastSearch.direction === "forward"
            ? "backward"
            : "forward"
          : state.lastSearch.direction) ?? "forward";
      const match = findNextMatch(state.buffer, state.cursor, state.lastSearch.query, direction);
      if (!match) {
        return { ...state, statusMessage: `パターンが見つかりません: ${state.lastSearch.query}` };
      }
      return { ...state, cursor: clampCursor(state.buffer, match) };
    }

    case "search-word": {
      const word = wordUnderCursor(state.buffer, state.cursor);
      if (!word) {
        return { ...state, statusMessage: "カーソル下に単語がありません" };
      }
      const match = findNextMatch(state.buffer, state.cursor, word, command.direction);
      const lastSearch = { query: word, direction: command.direction };
      if (!match) {
        return { ...state, lastSearch, statusMessage: `パターンが見つかりません: ${word}` };
      }
      return { ...state, cursor: clampCursor(state.buffer, match), lastSearch };
    }

    case "repeat-find": {
      if (!state.lastFind) {
        return { ...state, statusMessage: "直前の行内検索がありません" };
      }
      const { variant, char, direction } = state.lastFind;
      const effectiveDirection = command.reversed
        ? direction === "forward"
          ? "backward"
          : "forward"
        : direction;
      const motionName = effectiveDirection === "forward" ? variant : variant === "f" ? "F" : "T";
      const motion = MOTIONS.get(motionName);
      if (!motion) {
        return state;
      }
      const range = motion(state.buffer, state.cursor, null, char);
      return { ...state, cursor: clampCursor(state.buffer, range.end) };
    }

    case "put": {
      const registerName = command.register ?? UNNAMED_REGISTER;
      const content = getRegister(state.registers, registerName);
      if (!content || content.lines.length === 0) {
        return { ...state, statusMessage: "レジスタが空です" };
      }
      const history = pushUndoSnapshot(state.history, {
        buffer: state.buffer,
        cursor: state.cursor,
      });
      const result = applyPut(state.buffer, state.cursor, content, command.before);
      const cursor = clampCursor(result.buffer, result.cursor);
      return { ...state, buffer: result.buffer, cursor, history };
    }

    case "enter-visual":
      return { ...state, mode: command.variant, visualAnchor: state.cursor };

    case "set-mark":
      return { ...state, marks: withMark(state.marks, command.name, state.cursor) };

    case "jump-to-mark": {
      const target = getMark(state.marks, command.name);
      if (!target) {
        return { ...state, statusMessage: `マークが設定されていません: ${command.name}` };
      }
      return { ...state, cursor: clampCursor(state.buffer, target) };
    }

    case "start-macro-recording":
      return { ...state, macroRecording: { name: command.name, keys: [] } };

    case "repeat":
    case "play-macro":
      // reduce() が共有ステップバジェットで state.lastChange / マクロを再生する（ここには到達しない）。
      return state;

    default:
      return state;
  }
}
